import "altcha";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, } from "react";
import { useIntl } from "react-intl";
import { useOkapiKy } from "@folio/stripes/core";
/** Tag of the altcha web component (registered by the side-effect import above). */
var ALTCHA_ELEMENT = "altcha-widget";
/** HTTP header carrying the solved captcha (base64 widget payload) on a protected request (DB-876). */
export var ALTCHA_HEADER = "X-Altcha";
/** Widget text keys mapped to the translation ids that hold their Czech wording. */
export var ALTCHA_STRING_KEYS = {
    ariaLinkLabel: "stripes-core.altcha.ariaLinkLabel",
    error: "stripes-core.altcha.error",
    expired: "stripes-core.altcha.expired",
    footer: "stripes-core.altcha.footer",
    label: "stripes-core.altcha.label",
    verificationRequired: "stripes-core.altcha.verificationRequired",
    verified: "stripes-core.altcha.verified",
    verifying: "stripes-core.altcha.verifying",
    waitAlert: "stripes-core.altcha.waitAlert",
};
var NO_CACHE_HEADERS = {
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: "0",
};
/**
 * Self-hosted ALTCHA proof-of-work captcha for the anonymous forms of the login side (DB-876).
 * The challenge is fetched through the okapi ky client, so it carries the same tenant headers as
 * the other anonymous calls of the form; the solved payload goes to the protected request in the
 * {@link ALTCHA_HEADER} header.
 */
export var AltchaWidget = forwardRef(function (_a, ref) {
    var challengePath = _a.challengePath, onPayload = _a.onPayload;
    var elementRef = useRef(null);
    var onPayloadRef = useRef(onPayload);
    onPayloadRef.current = onPayload;
    // useOkapiKy() returns a new client on every render; the widget keeps one stable fetcher.
    var ky = useOkapiKy();
    var kyRef = useRef(ky);
    kyRef.current = ky;
    var formatMessage = useIntl().formatMessage;
    var strings = useMemo(function () {
        return JSON.stringify(Object.fromEntries(Object.entries(ALTCHA_STRING_KEYS).map(function (_a) {
            var key = _a[0], id = _a[1];
            return [
                key,
                formatMessage({ id: id }),
            ];
        })));
    }, [formatMessage]);
    var fetchChallenge = useCallback(function () { return kyRef.current.get(challengePath, { headers: NO_CACHE_HEADERS }); }, [challengePath]);
    useEffect(function () {
        if (elementRef.current) {
            elementRef.current.customfetch = fetchChallenge;
        }
    }, [fetchChallenge]);
    useEffect(function () {
        var element = elementRef.current;
        if (!element) {
            return undefined;
        }
        var handleStateChange = function (event) {
            var _a;
            var _b = (_a = event.detail) !== null && _a !== void 0 ? _a : {}, state = _b.state, payload = _b.payload;
            onPayloadRef.current(state === "verified" && payload ? payload : null);
        };
        element.addEventListener("statechange", handleStateChange);
        return function () {
            return element.removeEventListener("statechange", handleStateChange);
        };
    }, []);
    useImperativeHandle(ref, function () { return ({
        reset: function () {
            var element = elementRef.current;
            if (typeof (element === null || element === void 0 ? void 0 : element.reset) === "function") {
                element.reset();
            }
            onPayloadRef.current(null);
        },
    }); }, []);
    return (
    // createElement with the tag name instead of JSX: the build must not depend on the altcha
    // package's ambient JSX.IntrinsicElements augmentation being picked up by the type checker.
    React.createElement(ALTCHA_ELEMENT, {
        ref: elementRef,
        challengeurl: challengePath,
        strings: strings,
    }));
});
AltchaWidget.displayName = "AltchaWidget";
